export function isBST(date: Date): boolean {
  // BST starts on the last Sunday of March and ends on the last Sunday of October
  const year = date.getFullYear();
  const marchLastSunday = new Date(year, 2, 31); // March 31
  while (marchLastSunday.getDay() !== 0) { // While not Sunday
    marchLastSunday.setDate(marchLastSunday.getDate() - 1);
  }
  const octoberLastSunday = new Date(year, 9, 31); // October 31
  while (octoberLastSunday.getDay() !== 0) { // While not Sunday
    octoberLastSunday.setDate(octoberLastSunday.getDate() - 1);
  }
  
  // BST is active if date is between these Sundays at 1am UTC
  const startBST = new Date(marchLastSunday.getTime() + 3600000); // Add 1 hour for 1am
  const endBST = new Date(octoberLastSunday.getTime() + 3600000);
  
  return date >= startBST && date < endBST;
}

function lastSundayUTC(year: number, month: number): number {
  const d = new Date(Date.UTC(year, month + 1, 0)); // last day of the month
  return d.getUTCDate() - d.getUTCDay();
}

/**
 * Whether a STORED sensor timestamp was written during BST.
 * Sensors store UK wall-clock time with no zone, so the stored value (read as UTC)
 * is compared against wall-clock changeover times: BST starts when the clock jumps
 * 01:00 -> 02:00 on the last Sunday of March and ends when it falls back at 02:00
 * on the last Sunday of October. The repeated 01:00-01:59 hour in October is
 * ambiguous; it is treated as BST so it overlaps rather than leaving a gap.
 */
export function isStoredBST(date: Date): boolean {
  const year = date.getUTCFullYear();
  const start = Date.UTC(year, 2, lastSundayUTC(year, 2), 1);
  const end = Date.UTC(year, 9, lastSundayUTC(year, 9), 2);
  const t = date.getTime();
  return t >= start && t < end;
}

/**
 * Adjusts a stored sensor timestamp (UK wall-clock time) to a real instant for display
 * If the stored time is in BST, subtracts 1 hour
 * If the stored time is in GMT, returns the date as-is
 */
export function adjustTimestampForDisplay(utcDate: Date | string): Date {
  const date = new Date(utcDate);
  // If the stored wall-clock time is in BST, subtract 1 hour to get the real instant
  if (isStoredBST(date)) {
    return new Date(date.getTime() - 60 * 60 * 1000);
  }
  // If not in BST (GMT), return as-is
  return date;
}

/**
 * Adjusts a local date to UTC for API requests
 * If the date is in BST, adds 1 hour to convert from BST to UTC
 * If the date is in GMT, returns the date as-is
 */
export function adjustDateForAPI(localDate: Date): Date {
  // If the date is in BST, add 1 hour to convert from BST to UTC
  if (isBST(localDate)) {
    return new Date(localDate.getTime() + 60 * 60 * 1000);
  }
  // If not in BST (GMT), return as-is
  return localDate;
}

export function formatDateTime(date: Date | string, skipTimeAdjustment: boolean = false): string {
  // Create reading date from server timestamp
  const readingDate = new Date(date);
  
  // Use the reading time as a reference point and calculate how long ago it was
  // Since the client clock might be inaccurate, we'll use a more conservative approach
  const now = new Date();
  
  // Debug logging removed for performance

  // Calculate time difference
  const diff = now.getTime() - readingDate.getTime();
  const diffMinutes = Math.ceil(diff / 60000); // Use ceil to show "1m ago" for anything over 0 seconds
  const diffHours = Math.floor(diff / 3600000);
  const diffDays = Math.floor(diff / 86400000);

  // If the reading time appears to be in the future (client clock behind server)
  if (diffMinutes < 0) {
    return "1m ago"; // Changed from "Just now" to "1m ago"
  }

  // If less than 24 hours ago, show relative time
  if (diffDays < 1) {
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    }
    return `${diffHours}h ago`;
  }

  // If this year, show date without year
  if (readingDate.getFullYear() === now.getFullYear()) {
    return readingDate.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // If different year, show with year
  return readingDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateInBST(date: Date): string {
  // Check if the date is in BST period
  if (isBST(date)) {
    // Add 1 hour to convert from UTC to BST
    const bstDate = new Date(date.getTime() + 60 * 60 * 1000);
    return bstDate.toISOString();
  }
  // If not in BST, return as UTC (GMT)
  return date.toISOString();
}